import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CalendarDays,
  Pencil,
  Plus,
  Trash2,
  AlertCircle,
  Users,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useCreateMobilisation,
  useDeleteMobilisation,
  useMobilisationsList,
  useUpdateMobilisation,
} from '../../hooks/useMobilisations';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { MobilisationAttendeesModal } from '../../components/admin/MobilisationAttendeesModal';
import { MobilisationEditModal } from '../../components/admin/MobilisationEditModal';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { ApiError } from '../../lib/api';
import { formatDate } from '../../lib/dateFmt';

const ADMIN_ROLES = ['admin', 'coordinator'];

const STATUS_TONES = {
  planning: 'bg-surface-2 text-fg-muted',
  active: 'bg-success-soft/15 text-success-soft-fg',
  closed: 'bg-surface-inset text-fg-muted',
} as const;

export function MobilisationsAdmin() {
  const { data: user, isLoading } = useCurrentUser();
  const list = useMobilisationsList();
  const create = useCreateMobilisation();
  const update = useUpdateMobilisation();
  const remove = useDeleteMobilisation();
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [attendeesTarget, setAttendeesTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [editTarget, setEditTarget] = useState<{
    id: string;
    name: string;
    date: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  const allowed = user.roles.some((r) => ADMIN_ROLES.includes(r));
  if (!allowed) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Restricted to admin / coordinator.
      </main>
    );
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ name, date, status: 'planning' });
      toast.success(`Created mobilisation "${name}"`);
      setName('');
      setDate('');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'create failed';
      setError(msg);
      toast.error(msg);
    }
  }

  async function onSetStatus(id: string, status: 'planning' | 'active' | 'closed') {
    try {
      await update.mutateAsync({ id, status });
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'update failed');
    }
  }
  async function performDelete() {
    if (!deleteTarget) return;
    try {
      await remove.mutateAsync(deleteTarget.id);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'delete failed');
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <CalendarDays className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Mobilisations
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Create, edit, and switch between mobilisation events. Use
              the switcher in the top nav to scope the rest of the app to
              one of these.
            </p>
          </div>
        </header>

        <form
          onSubmit={onCreate}
          className="mt-6 rounded-lg border border-line/10 bg-surface p-5 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-fg-muted">
            Create a new mobilisation
          </h2>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="min-w-[200px] flex-1 text-sm">
              <span className="font-medium text-fg-muted">Name</span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. HCC Contract Go-Live 2026"
                className="mt-1 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
              />
            </label>
            <label className="text-sm">
              <span className="font-medium text-fg-muted">Date</span>
              <input
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 block h-10 rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
              />
            </label>
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={create.isPending}
              iconLeft={<Plus className="h-4 w-4" />}
            >
              Create
            </Button>
          </div>
          {error ? (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}
        </form>

        <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {list.isLoading ? (
            <SkeletonList rows={3} />
          ) : (list.data ?? []).length === 0 ? (
            <EmptyState
              icon={<CalendarDays className="h-6 w-6" />}
              title="No mobilisations yet"
              description="Create the first one above. The default status is 'planning' so you can stage attendees before the day."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-muted">
                  <tr>
                    <th className="px-4 py-2.5">Name</th>
                    <th className="px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(list.data ?? []).map((m) => (
                    <tr
                      key={m.id}
                      className="border-t border-line/10 transition-colors hover:bg-surface-2/50"
                    >
                      <td className="px-4 py-2.5 font-medium">{m.name}</td>
                      <td className="px-4 py-2.5 tabular-nums text-fg-muted">
                        {formatDate(m.date)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="inline-flex items-center gap-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                              STATUS_TONES[m.status as keyof typeof STATUS_TONES] ??
                              'bg-surface-2 text-fg-muted'
                            }`}
                          >
                            {m.status}
                          </span>
                          <select
                            value={m.status}
                            onChange={(e) =>
                              onSetStatus(
                                m.id,
                                e.target.value as 'planning' | 'active' | 'closed',
                              )
                            }
                            aria-label={`Status for ${m.name}`}
                            className="rounded-md border border-line-strong/20 bg-surface px-2 py-1 text-xs shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
                          >
                            <option value="planning">planning</option>
                            <option value="active">active</option>
                            <option value="closed">closed</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          {m.kind !== 'onboarding' ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                iconLeft={<Pencil className="h-3.5 w-3.5" />}
                                onClick={() =>
                                  setEditTarget({
                                    id: m.id,
                                    name: m.name,
                                    date: m.date,
                                  })
                                }
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                iconLeft={<Users className="h-3.5 w-3.5" />}
                                onClick={() =>
                                  setAttendeesTarget({ id: m.id, name: m.name })
                                }
                              >
                                Attendees
                              </Button>
                            </>
                          ) : null}
                          {user.roles.includes('admin') ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              iconLeft={<Trash2 className="h-3.5 w-3.5" />}
                              onClick={() => setDeleteTarget({ id: m.id, name: m.name })}
                              className="text-danger-soft-fg hover:bg-danger-soft/15"
                            >
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <MobilisationAttendeesModal
        mobilisation={attendeesTarget}
        onClose={() => setAttendeesTarget(null)}
      />

      <MobilisationEditModal
        // Keyed so the form re-seeds from whichever row opened it.
        key={editTarget?.id ?? 'closed'}
        mobilisation={editTarget}
        onClose={() => setEditTarget(null)}
      />

      <ConfirmModal
        open={!!deleteTarget}
        tone="destructive"
        title={`Delete "${deleteTarget?.name ?? ''}"?`}
        message={
          <>
            Cascades to attendee links for this mobilisation. Events on the
            chain are preserved — only the mobilisation record and its
            attendee assignments go.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Keep"
        onConfirm={performDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </main>
  );
}
